import {
  IconCheck,
  IconChevronLeft,
  IconChevronRight,
  IconCircleCheck,
  IconEye,
  IconRotateClockwise,
  IconPlayerSkipForward,
  IconScissors,
} from "@tabler/icons-react";
import { TrackFrame } from "@/types/trackLabeling";
import { SCROLL_SPEED_OPTIONS, type ScrollSpeed } from "@/config/constants";
import { HotkeyBadge } from "./shared";
import { TrackFrameViewer } from "@/components/track-frame-viewer/TrackFrameViewer";

/**
 * The CSS position of the slider's thumb at `percentage`, the same place the browser puts the
 * native thumb. The drawn thumb then sits exactly over the native one, so grabbing it anywhere
 * starts a drag, even at the first and last frame, i.e. make it much easier to drag the slider.
 */
const sliderPosition = (percentage: number) =>
  `calc(9px + (100% - 18px) * ${percentage / 100})`;

type TrackReviewCardProps = {
  frames: TrackFrame[];
  clampedCurrentFrame: number;
  totalFrames: number;
  safeTotalFrames: number;
  isTrackLoading: boolean;
  trackError: string | null;
  onPrevious: () => void;
  onNext: () => void;
  showJumpTooltip: boolean;
  jumpTooltipPercentage: number;
  onFrameSliderChange: (value: string) => void;
  isTrackingConfirmed: boolean;
  progressPercentage: number;
  validPercentage: number;
  onBadDetection: () => void;
  /** The labeler said the bounding box jumped, and picks the frame to end the track at. */
  isChoosingEndFrame: boolean;
  onStartChoosingEndFrame: () => void;
  onCancelChoosingEndFrame: () => void;
  /** Ends the track at the current frame. Only offered while choosing the end frame. */
  onEndTrack: () => void;
  onPrimaryConfirm: () => void;
  onResetTask: () => void;
  isPrimaryConfirmDisabled: boolean;
  /** True while an answer is being sent or the task released, so neither can happen twice. */
  isLocked: boolean;
  trackId: string | null;
  scrollSpeed: ScrollSpeed;
  onScrollSpeedChange: (speed: ScrollSpeed) => void;
};

export const TrackReviewCard = ({
  frames,
  clampedCurrentFrame,
  totalFrames,
  safeTotalFrames,
  isTrackLoading,
  trackError,
  onPrevious,
  onNext,
  showJumpTooltip,
  jumpTooltipPercentage,
  onFrameSliderChange,
  isTrackingConfirmed,
  progressPercentage,
  validPercentage,
  onBadDetection,
  isChoosingEndFrame,
  onStartChoosingEndFrame,
  onCancelChoosingEndFrame,
  onEndTrack,
  onPrimaryConfirm,
  onResetTask,
  isPrimaryConfirmDisabled,
  isLocked,
  trackId,
  scrollSpeed,
  onScrollSpeedChange,
}: TrackReviewCardProps) => {
  // The slider is green once the tracking is confirmed, red while choosing the end frame, and blue
  // otherwise. The frame counter is red while choosing the end frame, and blue otherwise.
  let sliderFillClass = "bg-[#91A1FF]";
  let sliderThumbClass = "border-[#91A1FF]";
  let frameCounterClass = "bg-[#F0F3FF] text-[#5A6EFA]";
  if (isTrackingConfirmed) {
    sliderFillClass = "bg-[#81C784]";
    sliderThumbClass = "border-[#81C784]";
  } else if (isChoosingEndFrame) {
    sliderFillClass = "bg-[#EF4444]";
    sliderThumbClass = "border-[#EF4444]";
    frameCounterClass = "bg-[#FEF2F2] text-[#B91C1C]";
  }

  // Digits on both sides of " / ", each a digit's width plus the letter spacing, and the padding.
  const frameCounterLength = String(totalFrames).length * 2 + 3;
  const frameCounterMinWidth = `calc(${frameCounterLength} * (1ch + 0.1em) + 20px)`;

  // Confirm answers "Yes" once the labeler has reached the last frame. Until then it says to watch to
  // the end, with how far they are.
  // While choosing the end frame, the labeler stops on the first wrong frame, and the button names
  // the frames before it, the ones kept.
  const keptFrameCount = clampedCurrentFrame - 1;
  let keepFramesLabel = `Keep frames 1-${keptFrameCount}`;
  if (keptFrameCount === 0) {
    keepFramesLabel = "No frames to keep";
  } else if (keptFrameCount === 1) {
    keepFramesLabel = "Keep frame 1";
  }

  let confirmLabel = "Yes, same product";
  if (isPrimaryConfirmDisabled) {
    confirmLabel =
      totalFrames > 0
        ? `Watch to the end · ${clampedCurrentFrame} / ${totalFrames}`
        : "Watch to the end";
  }
  // Skip and "No, it jumped" are hidden until the last frame, but keep their place, so the frame
  // keeps its size when they appear.
  const actionsRowClass = isPrimaryConfirmDisabled
    ? "grid-cols-[0px_1fr] gap-x-0"
    : "grid-cols-[100px_1fr] gap-x-3";
  const revealedActionClass = isPrimaryConfirmDisabled
    ? "invisible opacity-0"
    : "visible opacity-100";
  const confirmButtonClass = isPrimaryConfirmDisabled
    ? "bg-[#EEF1F6] text-[#7C8BA1]"
    : "bg-[#5A6EFA] text-white shadow-[0_6px_16px_-4px_rgba(90,110,250,0.3)] hover:bg-[#4d5ee6] hover:shadow-[0_8px_20px_-4px_rgba(90,110,250,0.4)]";

  return (
    <div className="custom-scrollbar flex max-h-full w-full max-w-[450px] shrink-0 flex-col overflow-y-auto overflow-x-hidden rounded-[28px] border border-[#F0F3F5] bg-white shadow-[0_10px_40px_-10px_rgba(0,0,0,0.04)] transition-all">
      <div className="flex h-12 shrink-0 items-center justify-between gap-3 border-b border-[#F8FAFC] px-6">
        <div className="flex items-center gap-2.5 text-[#8695A8]">
          <IconEye size={18} strokeWidth={2.5} />
          <span className="mt-0.5 text-[11px] font-bold uppercase tracking-[0.15em]">
            Track Review
          </span>
        </div>
        <div className="flex items-center gap-3">
          <div
            className="flex items-center gap-1 rounded-full bg-[#F4F7F9] p-0.5"
            role="radiogroup"
            aria-label="Scroll speed"
          >
            {SCROLL_SPEED_OPTIONS.map((speed) => {
              const isActive = speed === scrollSpeed;
              const isPlaidActive = isActive && speed === "plaid";
              const stateClasses = isPlaidActive
                ? "plaid-active"
                : isActive
                  ? "bg-white text-[#5A6EFA] shadow-sm"
                  : "text-[#94A3B8] hover:text-[#64748b]";
              return (
                <button
                  key={speed}
                  type="button"
                  role="radio"
                  aria-checked={isActive}
                  onClick={() => onScrollSpeedChange(speed)}
                  className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] transition-colors ${stateClasses}`}
                  title={
                    speed === "plaid"
                      ? "They've gone to plaid!"
                      : `Hold-to-scroll speed: ${speed}`
                  }
                >
                  {speed}
                </button>
              );
            })}
          </div>
          {trackId && (
            <span className="text-[11px] font-mono text-[#94A3B8]">
              {trackId}
            </span>
          )}
        </div>
      </div>

      <div className="relative h-[500px] w-full shrink-0 overflow-hidden bg-[#111624] lg:h-auto lg:min-h-[280px] lg:flex-[0_1_800px]">
        {frames.length > 0 && (
          <div className="relative z-10 h-full w-full">
            <TrackFrameViewer
              frames={frames}
              framePosition={clampedCurrentFrame}
              isTrackingConfirmed={isTrackingConfirmed}
            />
          </div>
        )}
        {!isTrackLoading && !trackError && frames.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center px-8 text-center">
            <span className="text-[13px] text-[#A7B2C2]">
              No frames found for this track.
            </span>
          </div>
        )}
        {isTrackLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-[#111624]">
            <span className="text-[13px] text-[#A7B2C2]">Loading track...</span>
          </div>
        )}
        {!!trackError && !isTrackLoading && (
          <div className="absolute inset-0 flex items-center justify-center px-8 text-center">
            <span className="text-[13px] text-[#FCA5A5]">{trackError}</span>
          </div>
        )}

        {clampedCurrentFrame > 1 && totalFrames > 0 && (
          <button
            onClick={() => onPrevious()}
            className="group absolute left-5 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-[#2D364A]/35 shadow-lg transition-all hover:bg-[#2D364A]/70"
          >
            <IconChevronLeft
              size={22}
              className="mr-0.5 text-white/70 transition-colors group-hover:text-white"
              strokeWidth={2}
            />
          </button>
        )}

        {clampedCurrentFrame < totalFrames && totalFrames > 0 && (
          <button
            onClick={() => onNext()}
            className="group absolute right-5 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-[#2D364A]/35 shadow-lg transition-all hover:bg-[#2D364A]/70"
          >
            <IconChevronRight
              size={22}
              className="ml-0.5 text-white/70 transition-colors group-hover:text-white"
              strokeWidth={2}
            />
          </button>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-3 px-5 py-2">
        <div className="group relative flex h-8 flex-1 items-center">
          {showJumpTooltip && (
            <div
              className="absolute -top-10 z-40 -translate-x-1/2 whitespace-nowrap rounded-lg bg-[#1e293b] px-2.5 py-1.5 text-[10px] font-bold text-white shadow-xl"
              style={{ left: sliderPosition(jumpTooltipPercentage) }}
            >
              Jumped to clearest frame
              <div className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-[#1e293b]" />
            </div>
          )}

          <input
            type="range"
            min="1"
            max={safeTotalFrames}
            value={clampedCurrentFrame}
            onChange={(event) => onFrameSliderChange(event.target.value)}
            className="absolute inset-0 z-30 h-full w-full cursor-pointer appearance-none opacity-0 disabled:cursor-not-allowed [&::-moz-range-thumb]:h-[18px] [&::-moz-range-thumb]:w-[18px] [&::-moz-range-thumb]:border-0 [&::-webkit-slider-thumb]:h-[18px] [&::-webkit-slider-thumb]:w-[18px] [&::-webkit-slider-thumb]:appearance-none"
            aria-label="Frame selector"
            disabled={totalFrames === 0}
          />

          <div
            className={`pointer-events-none relative h-1.5 w-full overflow-hidden rounded-full transition-colors duration-300 ${
              isTrackingConfirmed ? "bg-[#E57373]" : "bg-[#2F384C]"
            }`}
          >
            <div
              className={`absolute bottom-0 left-0 top-0 rounded-full transition-colors duration-300 ${sliderFillClass}`}
              style={{
                width: sliderPosition(
                  isTrackingConfirmed ? validPercentage : progressPercentage,
                ),
              }}
            />
          </div>

          <div
            className={`pointer-events-none absolute top-1/2 z-10 h-[18px] w-[18px] -translate-x-1/2 -translate-y-1/2 rounded-full border-[4px] bg-white shadow-sm transition-[transform,border-color] duration-300 group-hover:scale-110 ${sliderThumbClass}`}
            style={{ left: sliderPosition(progressPercentage) }}
          />
        </div>

        {totalFrames > 0 && (
          // As wide as the longest count ("82 / 82"), so the track beside it keeps its length as
          // the frame number grows a digit.
          <span
            className={`shrink-0 rounded-lg px-2.5 py-1 text-center text-[10px] font-bold tabular-nums tracking-[0.1em] transition-colors ${frameCounterClass}`}
            style={{ minWidth: frameCounterMinWidth }}
          >
            {clampedCurrentFrame} / {totalFrames}
          </span>
        )}
      </div>

      <div className="flex w-full shrink-0 flex-col">
        {!isTrackingConfirmed && !isChoosingEndFrame && (
          <div className="flex flex-1 flex-col gap-3 px-7 pb-5 pt-1">
            <h2 className="text-[18px] font-bold leading-snug tracking-[-0.01em] text-[#1e293b]">
              Did the highlight stay on the same product in every frame?
            </h2>

            {/* Until the last frame, Watch to the end is the only action. Then animate in the others:
                Skip slides in and fades in, Watch to the end shrinks and becomes Yes, and the "No, it
                jumped" link fades in. */}
            <div
              className={`grid motion-safe:transition-[grid-template-columns,column-gap] motion-safe:duration-[450ms] motion-safe:ease-out ${actionsRowClass}`}
            >
              <div
                className={`transition-[opacity,visibility] motion-safe:duration-300 motion-safe:delay-200 ${revealedActionClass}`}
              >
                <button
                  onClick={onBadDetection}
                  disabled={
                    isPrimaryConfirmDisabled || totalFrames === 0 || isLocked
                  }
                  className="group relative flex w-[100px] items-center justify-center gap-2 rounded-[14px] border border-[#E2E8F0] py-[10px] transition-all disabled:cursor-not-allowed disabled:opacity-30 focus:outline-none focus:ring-2 focus:ring-slate-200 hover:border-red-200 hover:bg-red-50"
                >
                  <HotkeyBadge hotkey="1" />
                  <IconPlayerSkipForward
                    size={16}
                    className="text-[#8695A8] group-hover:text-red-400"
                  />
                  <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#64748b] group-hover:text-red-500">
                    Skip
                  </span>
                </button>
              </div>

              <button
                onClick={onPrimaryConfirm}
                disabled={isPrimaryConfirmDisabled || isLocked}
                className={`relative flex min-w-0 items-center justify-center rounded-[14px] border border-transparent py-[10px] transition-colors duration-300 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#5A6EFA] ${confirmButtonClass}`}
              >
                {isPrimaryConfirmDisabled ? (
                  // How far through the track the slider is, filling the greyed button.
                  <span className="pointer-events-none absolute inset-0 overflow-hidden rounded-[14px]">
                    <span
                      className="absolute inset-y-0 left-0 bg-[#DDE3FF]"
                      style={{ width: `${progressPercentage}%` }}
                    />
                  </span>
                ) : (
                  <HotkeyBadge hotkey="Enter" className="text-[#5A6EFA]" />
                )}
                <span className="relative flex items-center gap-2">
                  {isPrimaryConfirmDisabled ? (
                    <IconEye size={18} strokeWidth={2.5} />
                  ) : (
                    <IconCircleCheck size={18} strokeWidth={2.5} />
                  )}
                  <span className="text-[11px] font-bold uppercase tracking-[0.1em]">
                    {confirmLabel}
                  </span>
                </span>
              </button>
            </div>

            <div
              className={`flex justify-center transition-[opacity,visibility] motion-safe:duration-300 motion-safe:delay-[400ms] ${revealedActionClass}`}
            >
              <button
                type="button"
                onClick={onStartChoosingEndFrame}
                // Stops Enter and Space here, so the page's shortcuts don't see them.
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.stopPropagation();
                  }
                }}
                disabled={
                  isPrimaryConfirmDisabled || totalFrames <= 1 || isLocked
                }
                className="rounded-md px-2 py-0.5 text-[12px] text-[#64748b] underline decoration-[#CBD5E1] underline-offset-4 transition-colors hover:text-[#B91C1C] disabled:cursor-not-allowed disabled:no-underline disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5A6EFA]/50"
              >
                No, it jumped to{" "}
                <span className="font-semibold">another product</span>
              </button>
            </div>
          </div>
        )}

        {!isTrackingConfirmed && isChoosingEndFrame && (
          <div className="flex flex-1 flex-col gap-3 px-7 pb-5 pt-1">
            <h2 className="text-[18px] font-bold leading-snug tracking-[-0.01em] text-[#1e293b]">
              Where does it jump?
            </h2>

            <div className="flex flex-col gap-3 rounded-[16px] bg-[#FEF2F2] p-4">
              <p className="text-[13px] font-semibold leading-[1.5] text-[#991B1B]">
                Find the first frame where the highlight is on another product.
              </p>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onCancelChoosingEndFrame}
                  className="relative shrink-0 rounded-[12px] border border-[#FECACA] bg-white px-4 py-[10px] text-[11px] font-bold uppercase tracking-[0.1em] text-[#991B1B] transition-colors hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-200"
                >
                  <HotkeyBadge hotkey="Esc" />
                  Back
                </button>
                <button
                  type="button"
                  // Ignores the second click of a double-click.
                  onClick={(event) => {
                    if (event.detail <= 1) {
                      onEndTrack();
                    }
                  }}
                  disabled={clampedCurrentFrame <= 1 || isLocked}
                  className="flex flex-1 items-center justify-center gap-2 rounded-[12px] bg-[#B91C1C] py-[10px] text-white transition-colors hover:bg-[#991B1B] disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-red-300 focus:ring-offset-2"
                >
                  <IconScissors size={15} strokeWidth={2.5} />
                  <span className="text-[11px] font-bold uppercase tracking-[0.1em]">
                    {keepFramesLabel}
                  </span>
                </button>
              </div>
            </div>
          </div>
        )}

        {isTrackingConfirmed && (
          <div className="flex flex-1 flex-col px-6 pb-5 pt-3">
            <div className="flex flex-1 flex-col items-center justify-center gap-2 rounded-[24px] border-[2px] border-dashed border-[#E6EBEF] bg-[#F8FAFC]">
              <div className="mb-1 flex h-10 w-10 items-center justify-center rounded-full bg-[#E2E8F0]">
                <IconCheck strokeWidth={3} className="h-5 w-5 text-[#64748b]" />
              </div>
              <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#8695A8]">
                Tracking Confirmed
              </span>
              <button
                onClick={onResetTask}
                className="group mt-1 flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[#94A3B8] transition-all hover:bg-[#F0F3FF] hover:text-[#5A6EFA] focus:outline-none"
              >
                <IconRotateClockwise
                  size={12}
                  strokeWidth={2.5}
                  className="transition-transform duration-300 group-hover:-rotate-90"
                />
                <span className="mt-px text-[10px] font-bold uppercase tracking-[0.15em]">
                  Undo
                </span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
